import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { cn } from "@babackapp/ui/lib/utils";
import { Trash2 } from "lucide-react";
import ImportPropertySelect from "./import-property-select";
import { ShopImportMappingColumnType } from "./import-settings-form-types";

type ImportMappingRowProps = {
  title: string;
  subtitle: string;
  index: number;
  mapTo: ShopImportMappingColumnType;
  regexSuccess?: string;
  updateMapTo: (mapTo: ShopImportMappingColumnType) => void;
  updateRegex: (regex: string) => void;
  updateIndex: (index: number) => void;
  remove: () => void;
};

export default function ImportMappingRow({
  title,
  subtitle,
  index,
  mapTo,
  regexSuccess,
  updateMapTo,
  updateRegex,
  updateIndex,
  remove,
}: ImportMappingRowProps) {
  // Rows coming from an example file carry the column name: their position in
  // the file is authoritative, so it stays read-only. Rows added by hand (or
  // rebuilt from a saved mapping) have no name and own their column number.
  const isManual = !title;

  return (
    <div
      className={cn(
        "bg-background border-border border rounded-md px-4 py-2 flex flex-col gap-2 opacity-50",
        mapTo && "opacity-100",
      )}
    >
      <div className="flex items-center gap-4">
        <div className="flex flex-1 flex-col">
          {isManual ? (
            <ColumnIndexInput value={index} onChange={updateIndex} />
          ) : (
            <>
              <span className="text-sm">{title}</span>
              {subtitle && (
                <span className="text-xs text-muted-foreground">
                  ({subtitle})
                </span>
              )}
            </>
          )}
        </div>
        <span className="text-muted-foreground text-sm flex-1 text-center">
          ➔
        </span>
        <ImportPropertySelect value={mapTo} onChange={updateMapTo} />
        {isManual && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 shrink-0"
            aria-label="Remove column"
            onClick={remove}
          >
            <Trash2 className="size-4" />
          </Button>
        )}
      </div>
      {mapTo === "resultColumn" && (
        <Input
          placeholder="Regex for success (e.g. ^true$)"
          value={regexSuccess || ""}
          onChange={(e) => updateRegex(e.target.value)}
          className="h-8 text-sm"
        />
      )}
    </div>
  );
}

type ColumnIndexInputProps = {
  value: number;
  onChange: (index: number) => void;
};

function ColumnIndexInput({ value, onChange }: ColumnIndexInputProps) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">Column</span>
      <Input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(Math.max(0, e.target.valueAsNumber || 0))}
        className="h-8 w-20 text-sm"
      />
    </label>
  );
}
